import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  ArrowRight,
  CaretDown,
  CaretUp,
  DotsSixVertical,
  Lock,
  Plus,
} from '@phosphor-icons/react';
import { Alert } from '@clay-run/glaze/alert';
import { Badge, BadgeIcon } from '@clay-run/glaze/badge';
import { Button } from '@clay-run/glaze/button';
import {
  CollapsibleCard,
  CollapsibleCardBody,
  CollapsibleCardHeader,
} from '@clay-run/glaze/collapsible-card';
import { Icon } from '@clay-run/glaze/icon';
import { IconButton } from '@clay-run/glaze/iconbutton';
import { InputField } from '@clay-run/glaze/input-field';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { ReorderableItemWrapper, ReorderableList } from '@clay-run/glaze/reorderable-list';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { Text } from '@clay-run/glaze/text';
import { addErrorToast, addToast } from '@clay-run/glaze/toast/toast-queue';

import { useApi } from '../../../_shared/hooks';
import { FILTER_OPERATORS_BY_TYPE } from '../../../_shared/flow/filter';
import { availableSegmentations } from '../../available-segmentations';
import { CAMPAIGN_MEMBER_FILTER_FIELDS } from '../../campaign-member-filter-fields';
import { describeConditions } from '../../describe-conditions';
import { adoptUpdatePlaysResult, savePlaySegmentationConditions } from '../../ensure-play-owned';
import { useLauncherCampaign, useLauncherCampaigns } from '../../launcher-api';
import { useLauncherMembers } from '../../launcher-api';
import { getLibrary, getPlaybook, getPlays } from '../../api-client';
import { CampaignCombobox } from '../CampaignCombobox';
import { coverageByCmStatus } from '../../play-flow-coverage';
import {
  createSegmentation,
  duplicateSegmentation,
  reorderWaterfall,
  saveSegmentationConditions,
  updatePlays,
} from '../../mutations';
import { isArchivedInLibrary, ownedRowUseText, pickerTag, type PlaybooksPickerTag } from '../../play-ownership';
import {
  selectQualifyMembersOpen,
  setQualifyMembersOpen,
  useMembersPanelCollapseStore,
} from '../../members-panel-collapse-store';
import { reorderDraft, rungDraftKey, type DraftRung } from '../../playbook-draft';
import {
  DUPLICATE_SEGMENTATION_COPY_ONLY_MESSAGE,
  getRungMenuItems,
  type RungMenuAction,
} from '../../rung-menu';
import {
  addPlaybookDraftRung,
  adoptSavedPlaybookDraft,
  ensurePlaybookDraft,
  draftRungMapsToSegmentation,
  persistLadderInsideExclusive,
  remapPlaybookDraftRungSegmentation,
  removePlaybookDraftRung,
  runExclusive,
  selectPlaybookDraft,
  usePlaybookDraftStore,
} from '../../playbook-draft-store';
import { matchedIdsForRungDraftScope } from '../../members-panel-scope';
import { applyLadderSaveToCache } from '../../playbook-query-cache';
import {
  libraryQueryKey,
  matchedMembersPrefixKey,
  playbookDetailQueryKey,
  playsQueryKey,
  segmentationDetailQueryKey,
} from '../../query-keys';
import { clearRungScope, selectRungScope, setRungScope, useRungScopeStore } from '../../rung-scope-store';
import { SAVE_GROUP_FIRST, SEQUENCE_UNAVAILABLE_NAME } from '../../sequence-card-menu';
import { setSequenceEditorMode } from '../../sequence-preview-store';
import { subtypeBadgeColor } from '../../subtype-badge-colors';
import { confirmedFanOutSet, pluralize } from '../../usage-sidebar-logic';
import { previewRungCounts, rungInlineCounts, type RungInlineCounts } from '../../rung-counts';
import { useFilterMatchedMembers, useMatchedMembersLadder } from '../../use-matched-members';
import { notReadyBadgeText } from '../../sequence-readiness';
import { useRungReadiness } from '../../use-rung-readiness';
import { isInteractiveRowTarget } from '../../row-click-guard';
import { reorderIds, rungOrderSignature } from '../../waterfall-reorder';
import { selectWaterfallSaving, setWaterfallSaving, useWaterfallSaveStore } from '../../waterfall-save-store';
import { useUsageSidebarDecision } from '../../use-usage-sidebar-decision';
import {
  originKeyOf,
  selectUsageSidebar,
  useUsageSidebarStore,
  type PendingUsageSidebar,
  type UsageSidebarOrigin,
} from '../../usage-sidebar-store';
import type {
  PlaybooksLibraryResponse,
  PlaybooksMatchedMembersLadderResponse,
  PlaybooksPlaybookDetailResponse,
  PlaybooksSegmentationSummary,
  PlaybooksUpdatePlaysRequest,
  PlaybookRung,
  SegmentationConditions,
  SequenceMapping,
} from '../../schemas';
import { KebabMenu } from '../KebabMenu';
import type { KebabMenuItem } from '../KebabMenu';
import { PushDrawer } from '../PushDrawer';
import { SEGMENTATION_UNAVAILABLE_NAME, SegmentationConditionsEditor } from './SegmentationConditionsEditor';
import { MembersPanelHeaderStatus } from './MembersPanel';

/**
 * Launcher V2 A2 U5 — the Qualify step (plan's `renderStep1`, v6 prototype
 * ~line 5776): seed-campaign context card, coverage-by-CM-status card,
 * Audience Rule accordion (an unsaved draft, previewed against real members
 * as of PR B U3), and the segmentation waterfall. The waterfall's drag reorder is REAL as of PR B / U4
 * (R6): optimistic locally, then persisted as a new main-line Playbook version
 * via `mutations.reorderWaterfall`, with a rollback if the write fails.
 *
 * ── DISCOVERY: no `GET /v2/plays/:id` (same gap `PlaybooksHome` already
 * hit and documented) — this step re-derives the current Play from the same
 * campaign-scoped `getPlays` list `PlaybooksHome` calls, filtered to
 * `playId`. A Play id is now always a real, persisted row (created via
 * `CreatePlaySidebar`'s `mutations.createPlay`, `POST /v2/plays`) — the
 * "not found" state below is only reachable for a stale/unknown id (e.g. a
 * bookmarked link to a Play that was never created, or the pre-navigation
 * cache-invalidation race `CreatePlaySidebar` already guards against), not
 * for an ordinary in-progress "no Playbook chosen yet" case.
 *
 * ── SCHEMA GAP (narrowed by PR B U3): `PlaybooksPlaySummarySchema` still
 * carries no audience-filter field, so the Audience Rule remains an UNSAVED
 * draft — persisting it is Phase 1. What is no longer a gap is EVALUATION: the
 * draft (held in `rung-scope-store`, shared with `MembersPanel`) and every
 * rung's Preview now run against real `launcher_sfdc_campaign_members` rows
 * through U2's matched-member endpoints. The coverage card still reports a
 * plain by-status tally of REAL members rather than a routed/suppressed/
 * catch-all split — see `play-flow-coverage.ts`.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Seed-campaign context card
// ─────────────────────────────────────────────────────────────────────────────

/**
 * terra-qyt round-3 fix U16 (F4) — the seed picker's own `useLauncherCampaigns()`
 * fetch (a full-library `GET /api/tools/launcher/campaigns`) lives in this
 * sibling component instead of inline in `ContextCard`, so `ContextCard` can
 * render it ONLY under `mode === 'seed'` — the hook never mounts, and the
 * request never fires, for a play-mode (`mode: 'locked'`) card. Props are
 * exactly what the inline block used: `campaignSfdcId` to compute the
 * combobox's selected value, `onSeedChange` forwarded verbatim.
 */
function SeedCampaignPicker({
  campaignSfdcId,
  onSeedChange,
}: {
  campaignSfdcId: string;
  onSeedChange?: (campaignSfdcId: string | null) => void;
}) {
  const { campaigns, isLoading: campaignsLoading, isError: campaignsError } = useLauncherCampaigns();

  return (
    <CampaignCombobox
      campaigns={campaigns}
      value={campaigns.find((c) => c.id === campaignSfdcId) ?? null}
      onChange={(campaign) => onSeedChange?.(campaign?.id ?? null)}
      loading={campaignsLoading}
      isError={campaignsError}
      ariaLabel="Choose a seed campaign"
      placeholder="Search campaigns…"
    />
  );
}

/**
 * terra-qyt.15 (U10) — `mode: 'locked'` is the unmodified play-mode card (a
 * Play's campaign is fixed for its whole run, never rep-editable here).
 * `mode: 'seed'` is playbook mode's picker: the rep chooses (or clears) the
 * campaign this Playbook is measured against via the shared
 * `CampaignCombobox`, writing the choice out through `onSeedChange` (wired
 * by `PlayFlowShell` to the `?campaignSfdcId=` URL param). Once a seed is
 * picked, the SAME badges/roster-as-of lines below render verbatim — the
 * only difference from locked mode is the header title/badge and the picker
 * itself.
 *
 * terra-qyt round-3 fix U16 (F4) — the picker (and its `useLauncherCampaigns()`
 * fetch) is a separate `SeedCampaignPicker` child rendered only when
 * `mode === 'seed'`, so a play-mode card never mounts that hook and never
 * issues the full-campaign-list request.
 */
function ContextCard({
  campaignSfdcId,
  mode,
  onSeedChange,
}: {
  campaignSfdcId: string;
  mode: 'locked' | 'seed';
  onSeedChange?: (campaignSfdcId: string | null) => void;
}) {
  const { campaign, isLoading, isError } = useLauncherCampaign(campaignSfdcId);
  const hasCampaign = campaignSfdcId !== '';

  return (
    <div className="rounded-lg border border-border-primary bg-bg-primary">
      <div className="flex items-center justify-between gap-2 border-b border-border-secondary p-4">
        <Text size="sm" weight="semibold">
          {mode === 'seed' ? 'Seed campaign' : 'Campaign'}
        </Text>
        {mode === 'locked' && (
          <Badge>
            <BadgeIcon type={Lock} />
            Locked
          </Badge>
        )}
      </div>
      <div className="flex flex-col gap-2 p-4">
        <Text size="xs" variant="tertiary">
          {mode === 'seed' ? "Counts on this page are measured against this campaign's snapshot." : 'Fixed for this run.'}
        </Text>
        {mode === 'seed' && (
          <SeedCampaignPicker campaignSfdcId={campaignSfdcId} onSeedChange={onSeedChange} />
        )}
        {mode === 'seed' && !hasCampaign && (
          <Text size="xs" variant="tertiary">
            Pick a seed campaign to measure this Playbook against. Nothing is sent; the campaign is only used to
            count members.
          </Text>
        )}
        {hasCampaign && isLoading && <LoadingSpinner size="sm" />}
        {hasCampaign && isError && <Alert status="error">Couldn&apos;t load this campaign.</Alert>}
        {hasCampaign && !isLoading && !isError && campaign && (
          <>
            <Text size="sm" weight="medium">
              {campaign.name}
            </Text>
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge color={subtypeBadgeColor(campaign.subtype)}>{campaign.subtype ?? campaign.type}</Badge>
              {/*
                `campaign.status` is the raw Salesforce Campaign.Status field
                ('Planning', 'Active', etc. — written by
                `src/services/launcher-campaign-sync.ts`), not the launcher's
                own `launcher_status` vocabulary. No color mapper's vocabulary
                covers the real SFDC status values, so this renders as a
                plain, uncolored Badge rather than claiming a color that
                doesn't mean anything for this field (Codex adversarial-review
                fix — see `subtype-badge-colors.ts`'s `launcherStatusBadgeStatus` doc).
              */}
              <Badge>{campaign.status}</Badge>
              <Badge>{campaign.member_count} members</Badge>
            </div>
            <Text size="xs" variant="tertiary" className="font-mono">
              {campaign.id}
            </Text>
            {campaign.members_last_synced_at && (
              <Text size="xs" variant="tertiary">
                Roster as of {new Date(campaign.members_last_synced_at).toLocaleString()}
              </Text>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Coverage-by-CM-status card (real data; see module doc for the schema gap
// that keeps this from being a real routed/suppressed/catch-all breakdown).
// ─────────────────────────────────────────────────────────────────────────────

function CoverageCard({ campaignSfdcId }: { campaignSfdcId: string }) {
  const { members, isLoading, isError } = useLauncherMembers(campaignSfdcId);
  const rows = useMemo(() => coverageByCmStatus(members), [members]);

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border-primary bg-bg-primary p-4">
      <Text size="sm" weight="semibold">
        Coverage by CM status
      </Text>
      {isLoading && <LoadingSpinner size="sm" />}
      {isError && <Alert status="error">Couldn&apos;t load campaign members.</Alert>}
      {!isLoading && !isError && rows.length === 0 && (
        <Text size="xs" variant="tertiary">
          No members synced yet.
        </Text>
      )}
      {!isLoading && !isError && rows.length > 0 && (
        <div className="flex flex-col">
          {rows.map((row, i) => (
            <div
              key={row.status}
              className={`flex items-center justify-between gap-2 py-1.5 ${i > 0 ? 'border-t border-border-secondary' : ''}`}
            >
              <Text size="xs" weight="medium">
                {row.status}
              </Text>
              <Text size="xs" variant="tertiary" className="tabular-nums">
                {row.count} · {row.pct}%
              </Text>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Segmentation waterfall
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Playbooks QA round 4, U6 (S2) / Decision 13 — the usage-sidebar origin for
 * ONE rung on this ladder, in the identical two-doorway shape
 * `SequencesStep.tsx`'s own `selectedRungOrigin` builds (`'play'` when a play
 * hosts this flow, `'playbook'` when the playbook is being authored
 * seed-first). `originKeyOf` treats the two identically — one editing
 * surface, two doorways — so the decision hook below matches either.
 *
 * Pure, module-scope, and takes only scalars so a rung row can build its own
 * origin without the whole card re-rendering when an unrelated prop changes.
 */
function rungUsageOrigin(input: {
  playId: string | undefined;
  campaignSfdcId: string;
  playbookId: string;
  rungId: string | null;
  rungDraftKey: string;
}): UsageSidebarOrigin {
  const { playId, campaignSfdcId, playbookId, rungId, rungDraftKey: draftKey } = input;
  return playId !== undefined
    ? { kind: 'play', playId, campaignSfdcId, playbookId, rungId, rungDraftKey: draftKey }
    : {
        kind: 'playbook',
        playbookId,
        seedCampaignSfdcId: campaignSfdcId === '' ? null : campaignSfdcId,
        rungId,
        rungDraftKey: draftKey,
      };
}

interface RungRowProps {
  rung: PlaybookRung;
  index: number;
  library: PlaybooksLibraryResponse | undefined;
  onPreview: (segmentationName: string) => void;
  /**
   * R7 inline counts — `null` renders an em-dash (loading/stale ladder,
   * never a guessed zero; see `rung-counts.ts`'s doc). `undefined` (the prop
   * simply omitted) renders nothing at all — reserved for a future call site
   * that has no ladder to offer, though every current call site passes one.
   */
  counts?: RungInlineCounts | null;
  /**
   * Stages this rung's removal from the playbook draft (PR C, U3C). `null`
   * for the catch-all row — it has no "Remove group" item at all, matching
   * the kebab-items split this component already made.
   */
  onRemove: (() => void) | null;
  /**
   * QA round 7, U9 (item 16) — "Duplicate segmentation": duplicates this
   * group's saved segmentation and adds the copy to this ladder. `null` for
   * the catch-all row, same reasoning as `onRemove` — `getRungMenuItems`
   * never emits a `'duplicate-segmentation'` action for it.
   */
  onDuplicate: (() => void) | null;
  /**
   * Round-3c QA fixes, H2 — `true` for exactly the one row whose `rung.id`
   * matches the "Where is this used" deep link's `&rung=` param
   * (`UsagePlacesList`, H1; `WaterfallCard` does the id match). Auto-expands
   * this row ONCE on mount (or when it newly becomes `true`), and never
   * re-collapses it or fights a later manual collapse — see the effect
   * below.
   */
  autoExpand: boolean;
  /**
   * U6 (R-Q1) — the campaign this rung's conditions are previewed against
   * when the rep clicks Apply inside the expanded editor, and the id this
   * row hands `SegmentationConditionsEditor` as its `previewScope`. Threaded
   * from `QualifyStep` through `WaterfallCard` (a sibling of `MembersPanel`,
   * not a descendant of it — same reasoning as `RungScope`'s own
   * `playbookId` field).
   */
  campaignSfdcId: string;
  playbookId: string;
  /**
   * U6 (S2) — `undefined` in playbook-authoring mode; threaded in so this
   * row can build the `'play'`-flavoured usage-sidebar origin its expanded
   * editor parks a gated conditions save under (see {@link rungUsageOrigin}).
   */
  playId?: string;
  /**
   * W2 (fix-round2-plan.md) — the whole-Playbook ladder, read only while this
   * row's OWN `rung-draft` preview is active (`isDraftPreviewActive` below),
   * to exclude members a higher-priority rung already claims from the raw
   * live filter match — see `matchedIdsForRungDraftScope`'s doc
   * (`members-panel-scope.ts`) for why that reduction is exactly what saving
   * these staged conditions would produce. Same value `counts` is computed
   * from (`rungInlineCounts(ladder, rung, headVersionId)` at the call sites
   * below), including the SAME optimistic-reorder guard: `undefined` while
   * `saving`, because during that window `ladder`/`headVersionId` still
   * describe the OLD priority order (see the `counts` prop's call-site
   * comment for the full explanation).
   */
  ladder: PlaybooksMatchedMembersLadderResponse | undefined;
  /** Same `expectedPlaybookVersionId` staleness guard `counts` relies on via `rungInlineCounts`. */
  headVersionId: string | null;
}

function RungRow({
  rung,
  index,
  library,
  onPreview,
  counts,
  onRemove,
  onDuplicate,
  autoExpand,
  campaignSfdcId,
  playbookId,
  playId,
  ladder,
  headVersionId,
}: RungRowProps) {
  const [expanded, setExpanded] = useState(false);
  /**
   * H2 — applies `autoExpand` exactly once. `appliedRef` resets at the TOP
   * of the mount effect, not only in cleanup (StrictMode-safe per
   * `docs/v4-system/strictmode-resilient-effects.md`), so this still expands
   * on the "real" post-StrictMode-double-invoke mount. Once applied, a rep
   * manually collapsing the row (the caret / header click below, plain
   * `setExpanded` calls) is never fought — this effect only ever calls
   * `setExpanded(true)`, and only the one time `appliedRef.current` is still
   * `false`.
   */
  const autoExpandAppliedRef = useRef(false);
  useEffect(() => {
    autoExpandAppliedRef.current = false;
  }, []);
  useEffect(() => {
    if (autoExpandAppliedRef.current) return;
    if (!autoExpand) return;
    setExpanded(true);
    autoExpandAppliedRef.current = true;
  }, [autoExpand]);
  /**
   * Live-QA fixes, A1 — expanding a rung ALWAYS lands on the real
   * `SegmentationConditionsEditor`; there is no separate "summary" pane to
   * land on first. The `pane` state (`'summary' | 'conditions'`) this row
   * used to carry — added by QA follow-ups U4 (item 7) alongside the
   * extracted editor, but never actually reached by anything other than the
   * kebab's "Edit conditions" item — is gone entirely, mirroring
   * `PlaybooksConfigPage`'s Library segmentation rows (whose `expand.body`
   * always renders the editor once open) and the prototype's own
   * "conditions are edited in place, never in a sidebar" decision
   * (`playbooks-prototype-v6.html:8418`). "Edit conditions" is now just a
   * shortcut for expanding the row — there is no second pane to route to.
   */
  const editConditions = () => setExpanded(true);
  // R2: tint the row whose Preview currently scopes `MembersPanel`. Identity is
  // the rung's stable `id` and nothing else — the same identity
  // `matchedIdsForRungScope` looks the ladder up by, so the tinted row and the
  // previewed members can never come apart. Never `segmentationId`: a
  // `segmentationId` can legitimately repeat across rungs (the seed's catch-all
  // borrows an earlier rung's), so comparing it would tint EVERY row sharing
  // that segmentation rather than the one actually previewed. Never `position`
  // either: a reorder rewrites it.
  const scope = useRungScopeStore(selectRungScope);
  const isPreviewActive = scope?.kind === 'rung' && scope.rungId === rung.id;
  const segmentation = library?.segmentations.find((s) => s.id === rung.segmentationId);
  // [R18][R20] The row now exists (U1 (b)) even for a Library piece an admin
  // archived while this play's ladder still references it — never a raw id.
  const segmentationName = segmentation?.name ?? SEGMENTATION_UNAVAILABLE_NAME;
  const segmentationArchived = segmentation !== undefined && isArchivedInLibrary(segmentation);
  const isMapped = rung.mapping !== null;
  // U6 (R-Q1) — this row's own live filter preview: true only while the
  // rung-scope store's `'rung-draft'` scope names THIS rung. Reads the SAME
  // `useFilterMatchedMembers` cache entry `MembersPanel`'s own rung-draft
  // branch reads for the identical (campaignSfdcId, groups, groupLogic) —
  // one Apply click, one request, shared by both consumers.
  const isDraftPreviewActive = scope?.kind === 'rung-draft' && scope.rungId === rung.id;
  const draftPreviewQuery = useFilterMatchedMembers(
    campaignSfdcId,
    isDraftPreviewActive ? scope.groups : [],
    isDraftPreviewActive ? scope.groupLogic : 'AND',
    isDraftPreviewActive,
  );
  // W2 (fix-round2-plan.md, Codex round-2 "Will Break #2") — the raw live
  // filter match, reduced by every ladder rung ABOVE this one's claimed
  // members before it becomes a count. Without this, a higher-priority
  // rung's claim leaks into this row's "(preview)" number even though Save
  // would never actually give this rung those members (first-match-wins).
  // See `matchedIdsForRungDraftScope`'s doc for the full argument.
  const draftBucketIds = isDraftPreviewActive
    ? matchedIdsForRungDraftScope(ladder, rung.id, headVersionId, draftPreviewQuery.data?.matchedMemberIds ?? null)
    : null;
  const draftPreviewCounts = isDraftPreviewActive ? previewRungCounts(draftBucketIds?.length ?? null, isMapped) : null;
  const sequence = rung.mapping ? library?.sequences.find((s) => s.id === rung.mapping!.sequenceId) : undefined;
  // R6 — v1-parity Ready badge (PR C, U5). `rung.mapping` here already carries
  // any staged sender edit: `QualifyStep`'s render loop merges the draft's
  // per-rung mapping onto the head rung before it ever reaches this row (see
  // that call site's comment), so this hook and `SequencesStep`'s rail card
  // can never disagree about the same rung's sender.
  const { readiness, reasons } = useRungReadiness(rung.mapping, library);
  /**
   * Live-QA fixes, A1 (decision #1) — the sequence-name subtitle that
   * replaces the old summary-pane echo of `mappingLabel`: the mapped
   * sequence's name, or "No sequence yet" when the rung has no mapping at all
   * (QA round 7, item 12 — "mapping" is implementation language; the rung has
   * or doesn't have a sequence). Rendered directly under the segmentation
   * name in the header, so it's visible without expanding the row.
   *
   * [R18][R20] plan 2026-09-28-006 U5 — the row now exists (U1 (b)) even for
   * a Library sequence an admin archived while this rung still maps it;
   * never a raw id.
   */
  const sequenceSubtitle = rung.mapping ? (sequence?.name ?? SEQUENCE_UNAVAILABLE_NAME) : 'No sequence yet';
  const sequenceArchived = sequence !== undefined && isArchivedInLibrary(sequence);

  // QA round 7, U9 (item 16) — the item SET (which actions, in which order,
  // with which label) lives in `rung-menu.ts#getRungMenuItems`, pinned by a
  // bun test; this row only maps each descriptor onto its own handler.
  const onSelectByAction: Record<RungMenuAction, () => void> = {
    'edit-conditions': editConditions,
    'duplicate-segmentation': () => onDuplicate?.(),
    remove: () => onRemove?.(),
  };
  const items: KebabMenuItem[] = getRungMenuItems(rung).map((d) => ({
    key: d.action,
    label: d.label,
    danger: d.danger,
    onSelect: onSelectByAction[d.action],
  }));

  return (
    <div
      className={`flex flex-col gap-2 border-b border-border-secondary p-3 transition-colors last:border-b-0 ${
        // 2026-09-16 Fix 3 — the selected-state token, not `bg-bg-yellow-light`.
        // "Being previewed" is a SELECTION, not a warning, and the yellow token
        // resolves to `--color-lemon-800` in dark mode, which reads as a
        // saturated mustard flood. One token pair for this affordance
        // everywhere — see `MembersPanel.tsx`'s header comment.
        isPreviewActive ? 'bg-bg-selected' : ''
      }`}
    >
      {/*
        Live-QA fixes, A2 — the whole header (not just the caret) toggles
        `expanded`. Guarded via `isInteractiveRowTarget` so a click that lands
        on Preview, the kebab, or the caret itself fires ONLY that control's
        own handler — see that helper's doc comment above `RungRowProps`.
        `tabIndex`/`onKeyDown` give keyboard users the same toggle without a
        second, redundant `role="button"` wrapping the real buttons already
        inside this row.
      */}
      <div
        className="flex cursor-pointer items-center gap-2"
        tabIndex={0}
        onClick={(e) => {
          if (isInteractiveRowTarget(e.target)) return;
          setExpanded((v) => !v);
        }}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          if (isInteractiveRowTarget(e.target)) return;
          e.preventDefault();
          setExpanded((v) => !v);
        }}
      >
        {rung.isCatchAll ? (
          <Icon type={Lock} size="xs" aria-label="Pinned last" />
        ) : (
          <Icon type={DotsSixVertical} size="xs" aria-label="Drag to change priority" />
        )}
        <Text size="xs" variant="tertiary" className="w-4 shrink-0 text-right tabular-nums">
          {index + 1}
        </Text>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1.5">
            <Text size="sm" weight="medium" className="truncate">
              {segmentationName}
            </Text>
            {/* [R18] A Library piece an admin archived while this play's
                ladder still references it — the group keeps sending, but
                can't be picked again and never offers "update the playbook". */}
            {segmentationArchived && <StatusBadge status="neutral">Archived in Library</StatusBadge>}
          </div>
          {/* Live-QA fixes, A1 (decision #1): the mapped sequence's name, or
              "No sequence yet" — see `sequenceSubtitle`'s doc comment above. */}
          <div className="flex min-w-0 items-center gap-1.5">
            <Text size="xs" variant="tertiary" className="truncate">
              {sequenceSubtitle}
            </Text>
            {sequenceArchived && <StatusBadge status="neutral">Archived in Library</StatusBadge>}
          </div>
        </div>
        {counts !== undefined && (
          // R7: matched-member counts without clicking Preview. `null` (a
          // stale/unresolved ladder — see `rung-counts.ts`) renders an
          // em-dash, never a guessed zero. U6 (R-Q1) — while this row's own
          // unsaved conditions are being previewed live, `draftPreviewCounts`
          // replaces the real ladder-derived `counts`, labelled "(preview)" so
          // the number can never be mistaken for the saved ladder's answer.
          // W2 (fix-round2-plan.md) — "(preview)" now means "unsaved
          // conditions, evaluated at this rung's current ladder position": the
          // count IS ladder-aware (`matchedIdsForRungDraftScope` excludes
          // members a higher-priority rung already claims), it just hasn't
          // been saved yet — it is no longer a standalone, un-excluded count.
          <Text size="xs" variant="tertiary" className="shrink-0 tabular-nums">
            {isDraftPreviewActive
              ? draftPreviewCounts
                ? `${draftPreviewCounts.inBucket} in bucket (preview) · ${draftPreviewCounts.sends} sends`
                : '— in bucket · — sends'
              : counts
                ? `${counts.inBucket} in bucket · ${counts.sends} sends`
                : '— in bucket · — sends'}
          </Text>
        )}
        {isMapped ? (
          <>
            <StatusBadge status="success">Mapped</StatusBadge>
            {/* R6: no badge at all for an unmapped rung (see `isMapped` branch
                below) — "not ready" is meaningless for a rung that was never
                supposed to send. */}
            {readiness === 'ready' && <StatusBadge status="success">Ready</StatusBadge>}
            {readiness === 'not_ready' && (
              <StatusBadge status="warning">{notReadyBadgeText(reasons, sequence?.channel ?? null)}</StatusBadge>
            )}
          </>
        ) : (
          // QA round 7 (item 12) — "No mapping" → "No sequence" ("mapping" is
          // implementation language; OQ2: Spencer may prefer "Has sequence"
          // for the `isMapped` branch above, but the row already shows the
          // sequence's name under the segmentation name, so that badge stays
          // secondary and unchanged).
          <StatusBadge status="neutral">No sequence</StatusBadge>
        )}
        {/*
          Live-QA round 2, A1 — the button flips to "Hide preview" while this
          row's own preview is active, and clicking it in that state clears
          the scope + returns the sequence editor to edit mode: the EXACT
          pair `MembersPanel`'s own "Hide preview" fires (see that
          component's Hide-preview `onClick`), so the two affordances can
          never disagree about what "hide" means. Deliberately does NOT
          collapse the members panel — the rep opened it; unscoping it back
          to the full roster is the whole gesture.
        */}
        <Button
          variant="inline-text"
          size="xs"
          onClick={() => {
            if (isPreviewActive) {
              clearRungScope();
              setSequenceEditorMode('edit');
            } else {
              onPreview(segmentationName);
            }
          }}
        >
          {isPreviewActive ? 'Hide preview' : 'Preview'}
        </Button>
        <KebabMenu items={items} ariaLabel={`More actions for ${segmentationName}`} />
        {/*
          U2 — belt-and-suspenders against the SVG-glyph guard gap
          (`isInteractiveRowTarget`'s doc comment): `stopPropagation` means
          the row header's own onClick never even sees this click regardless
          of whether `e.target` resolves to the `<button>` or the inner
          `<svg>`, and `data-row-control` gives the guard itself a second,
          independent way to recognize this control.
        */}
        <IconButton
          icon={expanded ? CaretUp : CaretDown}
          variant="ghost"
          size="xs"
          aria-label={expanded ? 'Collapse' : 'Expand'}
          data-row-control
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
        />
      </div>
      {expanded && (
        <div className="flex flex-col gap-1.5 pl-6">
          {/* Live-QA fixes, A1: the editor is now ALWAYS what an expanded row
              shows — including for the catch-all, whose conditions are just
              as editable as any ranked rung's (the explainer above is
              informational, not a substitute for the editor). */}
          {rung.isCatchAll && (
            <Text size="xs" variant="tertiary">
              The catch-all deliberately has no conditions: it claims every member the groups above did
              not.
            </Text>
          )}
          <SegmentationConditionsEditor
            segmentationId={rung.segmentationId}
            onCancel={() => setExpanded(false)}
            previewScope={{ rungId: rung.id, rungName: segmentationName, playbookId }}
            // U6 (S2) — where a GATED save parks its pending decision; the
            // hook that executes it is mounted once at `QualifyStep` level.
            usageOrigin={rungUsageOrigin({
              playId,
              campaignSfdcId,
              playbookId,
              rungId: rung.id,
              rungDraftKey: rungDraftKey(rung.id),
            })}
            playId={playId}
            // Plan 2026-09-27-006 U7b (Deferred to Implementation default) —
            // this row's own library summary already resolved `segmentation`
            // above; its `owningPlayId` is threaded straight through rather
            // than the editor doing its own query-client lookup.
            owningPlayId={segmentation?.owningPlayId ?? null}
            playbookId={playbookId}
            archivedInLibrary={segmentationArchived}
          />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// A staged, not-yet-saved rung (PR C, U3C) — added via "Add segmentation" this
// session but no server id yet. Deliberately a separate, simpler component
// rather than a generalized `RungRow`: it can never be dragged (no id to send
// to the reorder endpoint — `playbook-draft.ts`'s own doc comment), can never
// show inline counts (the ladder only ever describes PERSISTED state, per
// `rung-counts.ts`), and can never be Previewed (`RungScope` addresses a real
// `rung.id`). Keeping `RungRow` itself untouched in this respect is also what
// keeps `playbooks-waterfall-card-drag-disable.test.tsx` and
// `playbooks-qualify-preview-tint.test.tsx` passing unmodified.
// ─────────────────────────────────────────────────────────────────────────────

function PendingRungRow({
  rung,
  index,
  library,
  onRemove,
  onDuplicate,
  usageOrigin,
  playId,
  playbookId,
}: {
  rung: DraftRung;
  index: number;
  library: PlaybooksLibraryResponse | undefined;
  onRemove: () => void;
  /**
   * QA round 7, U9 (item 16) — "Duplicate segmentation" for a still-unsaved
   * rung: its SEGMENTATION is already a real, persisted identity (see the
   * comment below), so duplication works identically to a saved `RungRow`'s.
   */
  onDuplicate: () => void;
  /**
   * U6 (S2) — the pending rung's own origin. `rungId` is `null` here (this
   * rung has no server id yet) and `rungDraftKey` (`new:<n>`) is what stays
   * stable across the save that mints one — exactly the case
   * `UsageSidebarOrigin`'s doc comment describes.
   */
  usageOrigin: UsageSidebarOrigin;
  /**
   * Plan 2026-09-25-002, U7 (Decision 21) — this pending rung's segmentation
   * is already a real, play-owned identity in play mode (picked from the
   * library, which forks it, or freshly created with `owningPlayId`); its
   * conditions editor needs the same `?playId=` every other in-play save
   * carries. `undefined` in the playbook doorway.
   */
  playId?: string;
  /** Plan 2026-09-27-006 U7b — needed alongside `playId` for the editor's `ensurePlayOwned` fork. */
  playbookId?: string;
}) {
  // U4: same inline mechanism as `RungRow` — this rung is unsaved (no server
  // `id` yet), but its SEGMENTATION is a real, persisted identity (picked
  // from the library or freshly created via `AddSegmentationDrawerBody`), so
  // the shared `SegmentationConditionsEditor` works here unchanged. No
  // `expanded`/pane split like `RungRow`'s: a pending rung has no mapping
  // summary to fall back to (it isn't saved, so it can't be mapped yet), so
  // there is nothing else the expanded body would ever show.
  const [editingConditions, setEditingConditions] = useState(false);
  const segmentation = library?.segmentations.find((s) => s.id === rung.segmentationId);
  // [R18][R20] see `RungRow`'s identical treatment above — never a raw id.
  const segmentationName = segmentation?.name ?? SEGMENTATION_UNAVAILABLE_NAME;
  const segmentationArchived = segmentation !== undefined && isArchivedInLibrary(segmentation);
  // QA round 7, U9 — a pending rung is never the catch-all (`addDraftRung`
  // only ever stages ranked rungs), so its menu is always the full three;
  // `getRungMenuItems({ isCatchAll: false })` is what `rung-menu.ts` pins.
  const onSelectByAction: Record<RungMenuAction, () => void> = {
    'edit-conditions': () => setEditingConditions(true),
    'duplicate-segmentation': onDuplicate,
    remove: onRemove,
  };
  const items: KebabMenuItem[] = getRungMenuItems({ isCatchAll: false }).map((d) => ({
    key: d.action,
    label: d.label,
    danger: d.danger,
    onSelect: onSelectByAction[d.action],
  }));

  return (
    <div className="flex flex-col gap-2 border-b border-border-secondary bg-bg-secondary p-3 last:border-b-0">
      <div className="flex items-center gap-2">
        <Icon type={DotsSixVertical} size="xs" className="opacity-30" aria-hidden="true" />
        <Text size="xs" variant="tertiary" className="w-4 shrink-0 text-right tabular-nums">
          {index + 1}
        </Text>
        <Text size="sm" weight="medium" className="min-w-0 flex-1 truncate">
          {segmentationName}
        </Text>
        {segmentationArchived && <StatusBadge status="neutral">Archived in Library</StatusBadge>}
        <StatusBadge status="neutral">Not saved yet</StatusBadge>
        <KebabMenu items={items} ariaLabel={`More actions for ${segmentationName}`} />
      </div>
      {editingConditions && (
        <div className="pl-6">
          <SegmentationConditionsEditor
            segmentationId={rung.segmentationId}
            onSaved={() => setEditingConditions(false)}
            onCancel={() => setEditingConditions(false)}
            usageOrigin={usageOrigin}
            playId={playId}
            owningPlayId={segmentation?.owningPlayId ?? null}
            playbookId={playbookId}
            archivedInLibrary={segmentationArchived}
          />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Playbooks QA round 4, U3 (S3) — one read-only accordion entry per available
// segmentation. Collapsed by default; expanding reveals `describeConditions`'
// plain-text summary of the segmentation's stored filters. The "Add" action
// (still the whole reason this list exists) lives IN the header next to the
// name, so picking a segmentation never requires expanding it first — same
// two-tier "peek, then commit" shape as the rung rows above. `data-row-control`
// mirrors those rows' guard: Glaze's own `CollapsibleCardHeader` only wires
// its caret `Trigger`, so nothing here NEEDS the guard to avoid a double
// toggle today, but the whole-name-area click below (added for the same
// "click anywhere to expand" affordance every other accordion in this file
// has) would otherwise also fire when the click lands on Add.
// ─────────────────────────────────────────────────────────────────────────────

function SegmentationPickerEntry({
  segmentation,
  tag,
  useText,
  onPick,
}: {
  segmentation: PlaybooksSegmentationSummary;
  /**
   * Plan 2026-09-27-006 U7b (Requirement R6) — `null` outside a play; inside
   * a play, {@link pickerTag}'s verdict for this row ("Edited in this play"
   * when this play has already forked it, "Only in this play" for a
   * play-created original, `null` for an untouched Library row).
   */
  tag?: PlaybooksPickerTag;
  /**
   * Plan 2026-09-28-006 U5 (rulebook R18, R19) — {@link ownedRowUseText}'s
   * caption for an owned row (`tag != null`) — which groups of this play use
   * it, or "Not used by any group yet". `undefined` for a Library row (no
   * caption; the tag alone is `null` there).
   */
  useText?: string;
  onPick: (segmentationId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const lines = describeConditions(segmentation.conditions, CAMPAIGN_MEMBER_FILTER_FIELDS, FILTER_OPERATORS_BY_TYPE);

  return (
    <CollapsibleCard open={open} onOpenChange={setOpen}>
      <CollapsibleCardHeader>
        <div
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-2"
          tabIndex={0}
          onClick={(e) => {
            if (isInteractiveRowTarget(e.target)) return;
            setOpen((v) => !v);
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            if (isInteractiveRowTarget(e.target)) return;
            e.preventDefault();
            setOpen((v) => !v);
          }}
        >
          <Text size="sm" weight="medium" className="truncate">
            {segmentation.name}
          </Text>
          {/* [R11][R18][R19] "Edited in this play · Used by <group>" (or "Only
              in this play" / "Not used by any group yet" for an orphaned
              row) — the picker never hides or substitutes a copy; this tag
              is how the rep tells two same-named rows apart. */}
          {tag != null && <Badge color="blue">{useText === undefined ? tag : `${tag} · ${useText}`}</Badge>}
        </div>
        <Button
          variant="secondary"
          size="xs"
          data-row-control
          onClick={(e) => {
            e.stopPropagation();
            onPick(segmentation.id);
          }}
        >
          Add
        </Button>
      </CollapsibleCardHeader>
      <CollapsibleCardBody className="flex flex-col gap-1 p-4">
        {lines.map((line, index) => (
          <Text key={index} size="xs" variant="tertiary">
            {line}
          </Text>
        ))}
      </CollapsibleCardBody>
    </CollapsibleCard>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// "Add segmentation" chooser — reuses the library query the rest of this file
// already fetches (no second round trip) and `createSegmentation` for a
// net-new one. Picking an existing segmentation or creating a new one both end
// the same way: `addPlaybookDraftRung` stages a rung above the catch-all and
// IMMEDIATELY persists it (write-through, 2026-09-02 rip-out) — there is no
// "Save Playbook" step left to visit before the rung has a real server id.
// ─────────────────────────────────────────────────────────────────────────────

function AddSegmentationDrawerBody({
  playbookId,
  playId,
  library,
  rungs,
  pendingRungs,
  onClose,
}: {
  playbookId: string;
  /**
   * Plan 2026-09-25-002, U6 (Decision 13) — the hosting play, or `undefined`
   * in the playbook doorway. A segmentation created here inside a play is the
   * play's own (`owningPlayId`), and the ladder cache patch targets the
   * play-scoped playbook key.
   */
  playId?: string;
  library: PlaybooksLibraryResponse | undefined;
  rungs: PlaybookRung[];
  pendingRungs?: DraftRung[];
  onClose: () => void;
}) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  /**
   * Plan 2026-09-28-006 U5 (rulebook R11) — the rows this picker renders:
   * every Library row and every row this play OWNS (a copy, or a
   * play-created original), each its own row — never a Library row whose
   * copy (or a play-created original) is already a current or pending rung
   * on this ladder (a play never has two groups on one segmentation), and
   * never a copy that IS already a rung (excluded the same way — see
   * `available-segmentations.ts`'s own doc). A copy that is NOT currently a
   * rung stays offered, tagged {@link pickerTag} and captioned
   * {@link ownedRowUseText} — "Not used by any group yet" by construction,
   * since it would otherwise have been excluded above. Outside a play
   * (`playId === undefined`) this is byte-identical to the pre-U5 exclusion
   * (`availableSegmentations`'s own contract paragraph).
   */
  const available = availableSegmentations(library, rungs, pendingRungs ?? [], playId);
  const segmentationNameById = useMemo(
    () => new Map((library?.segmentations ?? []).map((s) => [s.id, s.name])),
    [library],
  );

  function addAndPersist(segmentationId: string) {
    void addPlaybookDraftRung(apiFetch, playbookId, { segmentationId })
      .then((response) => response && applyLadderSaveToCache(queryClient, playbookId, response, playId))
      .catch((error) => {
        addErrorToast({
          content: error instanceof Error ? error.message : 'Could not add this segment — try again.',
        });
      });
  }

  function pickExisting(segmentationId: string) {
    addAndPersist(segmentationId);
    onClose();
  }

  async function handleCreate() {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setCreating(true);
    try {
      // Plan 002, U6 (Decision 13) — inside a play the new segmentation is the
      // play's own; the playbook doorway's call (and wire body) is unchanged.
      const created =
        playId === undefined
          ? await createSegmentation(apiFetch, trimmed)
          : await createSegmentation(apiFetch, trimmed, { owningPlayId: playId });
      await queryClient.invalidateQueries({ queryKey: libraryQueryKey(playId) });
      addAndPersist(created.id);
      onClose();
    } catch (error) {
      addErrorToast({
        content: error instanceof Error ? error.message : 'Could not create the segment — try again.',
      });
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Text size="xs" variant="tertiary">
          Pick an existing segment to add to {playId === undefined ? "this playbook's groups" : "this play's groups"}.
        </Text>
        {library === undefined ? (
          <LoadingSpinner size="sm" />
        ) : available.length === 0 ? (
          <Text size="xs" variant="tertiary">
            Every segment in the library is already used by {playId === undefined ? "this playbook's groups" : "this play's groups"}.
          </Text>
        ) : (
          <div className="flex flex-col gap-2">
            {available.map((seg) => {
              const tag = playId === undefined ? null : pickerTag(seg, playId);
              return (
                <SegmentationPickerEntry
                  key={seg.id}
                  segmentation={seg}
                  tag={tag}
                  useText={
                    tag == null ? undefined : ownedRowUseText(seg.id, rungs, segmentationNameById, 'segmentation')
                  }
                  onPick={pickExisting}
                />
              );
            })}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 border-t border-border-secondary pt-4">
        <Text size="xs" variant="tertiary">
          Or create a new one.
        </Text>
        <InputField label="Name" value={newName} onChange={(e) => setNewName(e.target.value)} />
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            disabled={newName.trim().length === 0}
            loading={creating}
            onClick={() => void handleCreate()}
          >
            Create &amp; add
          </Button>
        </div>
      </div>
    </div>
  );
}

export function WaterfallCard({
  campaignSfdcId,
  playbookId,
  playId,
  headVersionId,
  rungs,
  library,
  ladder,
  onContinue,
  pendingRungs,
  preselectRungId,
}: {
  /**
   * U6 (R-Q1) — threaded through to each `RungRow` so its expanded editor's
   * live "Apply" preview can ask `useFilterMatchedMembers` for THIS
   * campaign's members. `WaterfallCard` has no other route to it — it never
   * fetches campaign data itself.
   */
  campaignSfdcId: string;
  playbookId: string;
  /**
   * U6 (S2) — the hosting play, or `undefined` in playbook-authoring mode.
   * Threaded through to each rung row purely so it can build the correctly
   * flavoured usage-sidebar origin (`'play'` vs `'playbook'`) for a gated
   * conditions save — see {@link rungUsageOrigin}. Optional and additive, so
   * every pre-U6 test that renders this card directly keeps compiling and
   * keeps producing the playbook-flavoured origin.
   */
  playId?: string;
  /**
   * The playbook version id `rungs` was actually read from (`GET
   * /v2/playbooks/:id`'s `head.versionId`) — threaded through to
   * `reorderWaterfall` so the server can detect a head that moved since this
   * card last loaded (Codex adversarial-review fix, PR B follow-up). `null`
   * only while the playbook has no version yet (mid-create); a reorder can't
   * meaningfully happen with an empty ladder in that state. ALSO the
   * `expectedPlaybookVersionId` `rungInlineCounts` (R7) checks each row's
   * ladder entry against — the same version-staleness guard the reorder
   * write already relies on.
   */
  headVersionId: string | null;
  rungs: PlaybookRung[];
  library: PlaybooksLibraryResponse | undefined;
  /**
   * R7: the whole-Playbook first-match-wins ladder (`QualifyStep`'s
   * `useMatchedMembersLadder(campaignSfdcId, playbookId)` — same cache key
   * `MembersPanel`/`ReviewStep` share, so no extra fetch). `undefined` while
   * loading; each row resolves its own slice via `rungInlineCounts`.
   */
  ladder: PlaybooksMatchedMembersLadderResponse | undefined;
  onContinue: () => void;
  /**
   * Rungs staged in the playbook draft that have NOT been saved yet — no
   * server-minted `id`, so they cannot ride the `rungs: PlaybookRung[]` prop
   * above (that type requires one). Optional and additive on purpose: the
   * pre-U3C test suites render this card with a bare `rungs` prop and no
   * draft ever seeded, and omitting this prop must render exactly what it
   * did before (an empty pending block, i.e. nothing) — see
   * `playbooks-waterfall-card-drag-disable.test.tsx` /
   * `playbooks-qualify-preview-tint.test.tsx`, both unmodified by this
   * unit. They are NEVER part of the drag list (`playbook-draft.ts`'s own
   * doc: "an unsaved rung is never in the reorder drag list") — rendered as
   * their own non-draggable block, always directly above the catch-all,
   * mirroring exactly where `addDraftRung` inserts them.
   */
  pendingRungs?: DraftRung[];
  /**
   * Round-3c QA fixes, H2 — the "Where is this used" deep link's `&rung=`
   * param (`UsagePlacesList`, H1), matched against each row's `rung.id`
   * below to auto-expand exactly one row (`RungRow`'s `autoExpand` prop).
   * Optional and additive on purpose, same reasoning as `pendingRungs`
   * above: pre-H2 test suites render this card with no such prop and must
   * keep rendering with every row collapsed, exactly as before.
   */
  preselectRungId?: string | null;
}) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  /**
   * Write-through "Remove group" (2026-09-02 rip-out, U4) — mirrors
   * `AddSegmentationDrawerBody#addAndPersist`: applies the removal locally
   * (optimistic, via `removePlaybookDraftRung`'s own `persistLadder` call)
   * and immediately posts it, adopting the response into the playbook/
   * matched-members caches on success or surfacing a toast on failure. There
   * is no more "staged for removal, saved later on Review" state — see
   * `playbook-draft.ts`'s module doc.
   */
  function removeAndPersist(draftKey: string) {
    void removePlaybookDraftRung(apiFetch, playbookId, draftKey)
      .then((response) => response && applyLadderSaveToCache(queryClient, playbookId, response, playId))
      .catch((error) => {
        addErrorToast({
          content: error instanceof Error ? error.message : 'Could not remove this group — try again.',
        });
      });
  }

  /**
   * QA round 7, U9 — "Duplicate segmentation": a new saved segmentation
   * (server copies the head's conditions, names it "<name> (copy)") plus a
   * new group in THIS ladder, mapped like the source. Two writes, in order,
   * both through paths that already exist (`duplicateSegmentation` is the
   * Library kebab's; `addPlaybookDraftRung` is "Add segmentation"'s — same
   * `persistLadder` serialisation, so a reorder in flight is respected). The
   * new group lands where "Add segmentation" lands — just above the
   * catch-all (OQ14) — and the toast says so.
   *
   * Codex fixes 2026-09-18, F1/F2 — two writes means three outcomes, and the
   * toast has to name the one that happened. Mirrors `handleSegmentationSaveNew`
   * below (this file's fork-then-remap handler), which already splits "the
   * fork is saved; only the second step failed" from "nothing happened":
   *  - `duplicateSegmentation` throws → nothing exists; the generic error, and
   *    a retry is safe.
   *  - `addPlaybookDraftRung` returns `null` (`persistLadderOnce`'s "this
   *    ladder is no longer the one on screen / never loaded" guard) or THROWS
   *    → the copy IS in the Library but this ladder was not changed. Say so
   *    (`DUPLICATE_SEGMENTATION_COPY_ONLY_MESSAGE`); a bare success toast here
   *    was F1, the generic "could not duplicate" was F2.
   *  - both land → the success toast.
   * No cleanup/archive of the copy on a step-2 failure: that throw can be a
   * timeout on a write that actually landed, and archiving the copy would then
   * archive a segmentation this ladder references. The sibling handler makes
   * the same call for the same reason.
   */
  async function duplicateAndPersist(rung: { segmentationId: string; mapping: SequenceMapping | null }) {
    let created: Awaited<ReturnType<typeof duplicateSegmentation>>;
    try {
      // Plan 002, U6 (Decision 13) — inside a play the copy is the play's own.
      created =
        playId === undefined
          ? await duplicateSegmentation(apiFetch, rung.segmentationId)
          : await duplicateSegmentation(apiFetch, rung.segmentationId, { owningPlayId: playId });
    } catch (error) {
      addErrorToast({
        content: error instanceof Error ? error.message : 'Could not duplicate this segment — try again.',
      });
      return;
    }
    let response: Awaited<ReturnType<typeof addPlaybookDraftRung>>;
    try {
      // `invalidateQueries` resolves even when the refetch fails, so a throw
      // out of this block is the ladder write's.
      await queryClient.invalidateQueries({ queryKey: libraryQueryKey(playId) });
      response = await addPlaybookDraftRung(apiFetch, playbookId, {
        segmentationId: created.id,
        mapping: rung.mapping ?? undefined,
      });
    } catch {
      addErrorToast({ content: DUPLICATE_SEGMENTATION_COPY_ONLY_MESSAGE });
      return;
    }
    if (!response) {
      addErrorToast({ content: DUPLICATE_SEGMENTATION_COPY_ONLY_MESSAGE });
      return;
    }
    applyLadderSaveToCache(queryClient, playbookId, response, playId);
    addToast({ content: 'Duplicated — the copy was added above the catch-all group.', status: 'success' });
  }
  // Module-scope store, not local `useState` (Codex round-4 Finding #1) — see
  // `waterfall-save-store.ts`'s header for why this lock must survive
  // `WaterfallCard` unmounting when the stepper navigates away from Qualify
  // mid-save.
  const saving = useWaterfallSaveStore(selectWaterfallSaving(playbookId));

  // The catch-all is pinned last and never enters the drag list (mirrors the
  // prototype's `draggable="false"` on a catch-all rung) — only the ranked
  // rungs above it are reorderable.
  const catchAll = rungs.find((r) => r.isCatchAll) ?? null;
  const reorderable = useMemo(() => rungs.filter((r) => !r.isCatchAll), [rungs]);
  const reorderableIds = useMemo(() => reorderable.map((r) => r.id), [reorderable]);

  const [order, setOrder] = useState<string[]>(reorderableIds);
  // Dependency is deliberately a derived STRING, not `reorderableIds` itself —
  // resets the local drag order only when the server's ladder actually changes
  // (new/removed rung, a fresh Playbook fetch, or a persisted reorder), not on
  // every render that happens to produce a new (but equal) array reference.
  //
  // `rungOrderSignature` is the joined stable-id list, so it changes exactly
  // when the ORDER changes. A position-based fingerprint could not do this job:
  // a persisted reorder renumbers positions back to `0..n-1`, so the refetched
  // ladder carries the identical position SET in a different order.
  //
  // `headVersionId` is folded in as well, because it changes on every persisted
  // reorder (the success path in `handleReorder` below writes the new version
  // id into the playbook cache) — a second, independent signal that the server
  // data underneath this card was replaced.
  const reorderableSignature = rungOrderSignature(reorderable);
  const reorderResetKey = `${headVersionId}|${reorderableSignature}`;
  useEffect(() => {
    setOrder(reorderable.map((r) => r.id));
    // reorderResetKey is a derived summary of `reorderable` + `headVersionId`
    // (the real, intentional dependency) — see the comment above.
  }, [reorderResetKey]);

  const byId = useMemo(() => new Map(reorderable.map((r) => [r.id, r])), [reorderable]);
  const orderedRungs = order.map((id) => byId.get(id)).filter((r): r is PlaybookRung => Boolean(r));

  /**
   * Optimistic locally, persisted on drop (R6): the new order is shown
   * immediately, then written as a new main-line Playbook version. On failure
   * the local order rolls back to what the server still holds, so the card
   * never sits on an order that isn't saved.
   *
   * Guarded against a second drag composing against stale ids while the first
   * write is in flight two ways (Codex adversarial-review fix, PR B
   * follow-up): the drag surface itself is disabled via `ReorderableList`'s
   * `disabled={saving}` below, which is the primary fix (a second drag simply
   * cannot start); `expectedVersionId` on the request is the backstop that
   * protects data integrity even if disabling the surface were somehow
   * bypassed (a second tab, another user, or a UI regression).
   *
   * ── Codex round-2 finding #4 — `saving` must not clear (re-enabling the
   * drag surface) until the data a retry would depend on has actually landed,
   * and a rejected write must leave the client able to retry with FRESH data
   * rather than the same stale `headVersionId`:
   *
   *  - the SUCCESS path applies the response's own `versionId`/`rungs`
   *    directly to the playbook cache — no need to wait on a second
   *    `GET /v2/playbooks/:id` round trip for data this response already
   *    carries — and AWAITS the matched-member ladder invalidation (which
   *    genuinely does need a refetch, since the response doesn't carry
   *    per-rung matches) before falling into `finally`.
   *  - the CATCH path (a 409 `version_conflict`, or any other failure)
   *    invalidates both reads, so the very next drag — whether the user
   *    retries immediately or a moment later — composes against the current
   *    `headVersionId` instead of looping on the one that just got refused.
   *
   * ── SHARES `persistLadder`'s PER-PLAYBOOK QUEUE (Bug 3 fix, Codex
   * adversarial review, 2026-09-02): the `/reorder` POST below is wrapped in
   * `runExclusive(playbookId, ...)` — the SAME per-playbook FIFO queue every
   * write-through add/remove/mapping/sender save goes through
   * (`playbook-draft-store.ts#persistLadder`). Before this fix, reorder's only
   * concurrency gate was this component's own local `saving` boolean, entirely
   * uncoordinated with that queue — a drag-reorder could race a concurrent
   * add/remove/mapping-change on the SAME playbook, with both in flight
   * against the server at once. `runExclusive` makes the two mechanisms one
   * true queue: whichever fired first (reorder or a ladder edit) has its
   * response land before the other's request is even sent. The endpoint and
   * request shape here are unchanged — only when the request is allowed to
   * fire.
   */
  async function handleReorder(event: DragEndEvent) {
    const overId = event.over?.id;
    if (overId == null) return;
    const previous = order;
    const next = reorderIds(previous, String(event.active.id), String(overId));
    // A drag that lands where it started is a no-op — don't spend a version on it.
    if (next.join('|') === previous.join('|')) return;
    if (headVersionId === null) {
      // No version yet to reorder against (mid-create) — a drag surface with
      // nothing to reconcile against, refuse rather than send a request the
      // server schema will reject anyway.
      addErrorToast({ content: 'This Playbook has no saved version yet — reload and try again.' });
      return;
    }

    setOrder(next);
    setWaterfallSaving(playbookId, true);
    try {
      // ── THE WHOLE response-handling sequence runs INSIDE `runExclusive`'s
      // task (Bug 3 fix, follow-up hardening found while writing this fix's
      // own test) — not just the `reorderWaterfall` network call. Wrapping
      // only the network call frees the queue's slot for the NEXT queued op
      // (e.g. a concurrent "Remove group") the instant this response lands
      // on the wire, which can then run its OWN `adoptSavedPlaybookDraft`
      // BEFORE this reorder's — after which THIS reorder's own late adopt
      // (below) would overwrite that more-recent state, resurrecting a rung
      // the other op just removed. Keeping the cache update, the ladder
      // invalidation, and the adopt itself all inside the exclusive task is
      // what makes "the second op's request fires only after the first's
      // response has fully landed" also true of the STATE the first op's
      // response produces, not just of the wire request/response pair.
      await runExclusive(playbookId, async () => {
        if (playId !== undefined) {
          // Plan 2026-09-25-002, U6 (Key Technical Decision 16) — a play has no
          // `/reorder` endpoint: the reorder is a full-ladder write of the
          // play's own ladder (`POST /plays/:id/ladder`) through the draft
          // store, which adopts the response itself. Same exclusive task as the
          // playbook branch below, so the cache patch and the matched-members
          // refetch land before the next queued ladder op runs.
          // `persistLadderInsideExclusive` (not `persistLadder`) because this
          // task already holds the queue. `playId` is passed explicitly (F1
          // fix, play isolation) — it is this closure's OWN captured value
          // (the prop as of the drag), not re-read from the store once this
          // task's turn in the queue arrives.
          const saved = await persistLadderInsideExclusive(apiFetch, playbookId, playId ?? null, (draft) =>
            reorderDraft(draft, next),
          );
          if (saved) await applyLadderSaveToCache(queryClient, playbookId, saved, playId);
          return;
        }
        const response = await reorderWaterfall(apiFetch, playbookId, next, headVersionId);
        // Apply the server's own response directly — it already carries the new
        // main-line version id and the renumbered rungs (the same shape
        // `GET /v2/playbooks/:id` would report), so there is nothing left to
        // learn from a second round trip.
        queryClient.setQueryData(
          playbookDetailQueryKey(playbookId, playId),
          (old: PlaybooksPlaybookDetailResponse | undefined) =>
            old
              ? {
                  ...old,
                  head: {
                    versionId: response.versionId,
                    versionNumber: response.versionNumber,
                    rungs: response.rungs,
                  },
                }
              : old,
        );
        // The matched-member ladder is keyed by (campaignSfdcId, playbookId), not
        // by version — a reorder changes which rung claims whom under
        // first-match-wins, so the cached ladder is now wrong until refetched. Prefix
        // match (no campaignSfdcId in scope here) is fine: this is the only
        // write path that stales the ladder. AWAITED (finding #4): `saving`
        // must stay true — the drag surface disabled — until the fresh ladder
        // has actually landed, not merely been requested.
        await queryClient.invalidateQueries({ queryKey: matchedMembersPrefixKey() });
        // Reorder is a sibling write-through endpoint (`/reorder`, not
        // `persistLadder`) — once it resolves, adopt its response into the
        // playbook draft store the exact same way `persistLadder` adopts its
        // own save responses (`adoptSavedPlaybookDraft`'s own doc comment names
        // this as its second caller), so the draft mirrors the new head instead
        // of holding a now-stale rung order.
        //
        // `playbookId` is passed because this function is a plain async that
        // SURVIVES this card unmounting: the rep can navigate to a different Play
        // while the request is in flight, and applying this playbook's head to
        // that Play's draft would wipe its ladder. `adoptSavedPlaybookDraft`
        // no-ops unless the open draft is this playbook's (Codex
        // adversarial-review fix, PR C — the guard it used to be credited with
        // did not exist). This branch only runs when `playId === undefined`
        // (see the guard above) — `null` here (F1 fix) is explicit about that:
        // if the rep has since navigated INTO a Play of this same playbook,
        // `state.playId` is now that Play's id, not `null`, and this stale
        // template-reorder response correctly no-ops instead of clobbering it.
        adoptSavedPlaybookDraft(playbookId, { versionId: response.versionId, rungs: response.rungs }, null);
      });
    } catch (err) {
      setOrder(previous);
      // A version conflict means `headVersionId` is now stale (finding #4):
      // refetch both reads so the next drag — composed from this
      // component's next render — uses the current head instead of retrying
      // against the one that was just refused.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: playbookDetailQueryKey(playbookId) }),
        queryClient.invalidateQueries({ queryKey: matchedMembersPrefixKey() }),
      ]);
      addErrorToast({
        content: err instanceof Error ? err.message : 'Could not save the new order — try again.',
      });
    } finally {
      setWaterfallSaving(playbookId, false);
    }
  }

  return (
    <CollapsibleCard open={open} onOpenChange={setOpen} className="border-border-primary">
      <CollapsibleCardHeader>
        {/*
          Live-QA fixes, A2 — whole-header click-to-expand. Attached to BOTH
          of `CollapsibleCardHeader`'s direct children (rather than wrapping
          them in one new container div) deliberately: introducing a wrapper
          here that also carried `items-start` would shadow
          `playbooks-qualify-defaults.test.tsx`'s `cardHeader()` helper
          (`screen.getByText(title).closest('div.flex.items-start')`), which
          needs to keep resolving to Glaze's OWN header div (the one that
          actually carries the real Collapse/Expand trigger) — see that
          helper. Neither child below carries `items-start` today, so this
          adds the toggle without disturbing that selector.
        */}
        <div
          className="flex min-w-0 flex-1 cursor-pointer flex-col gap-0.5"
          tabIndex={0}
          onClick={(e) => {
            if (isInteractiveRowTarget(e.target)) return;
            setOpen((v) => !v);
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            if (isInteractiveRowTarget(e.target)) return;
            e.preventDefault();
            setOpen((v) => !v);
          }}
        >
          <Text size="sm" weight="semibold">
            Segmentations
          </Text>
          <Text size="xs" variant="tertiary">
            Members are routed to the first group below whose conditions match, in priority order.
          </Text>
        </div>
        <div
          className="flex shrink-0 cursor-pointer items-center gap-2"
          tabIndex={0}
          onClick={(e) => {
            if (isInteractiveRowTarget(e.target)) return;
            setOpen((v) => !v);
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            if (isInteractiveRowTarget(e.target)) return;
            e.preventDefault();
            setOpen((v) => !v);
          }}
        >
          {/*
            R3: always mounted (never conditionally added/removed) so the
            "Add segmentation" button never shifts when saving starts/ends —
            only opacity toggles, and `aria-hidden` keeps it out of the a11y
            tree while idle.
          */}
          <Text
            size="xs"
            variant="tertiary"
            aria-hidden={!saving}
            className={`transition-opacity ${saving ? 'opacity-100' : 'opacity-0'}`}
          >
            Saving order…
          </Text>
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Icon type={Plus} />
            Add segment
          </Button>
        </div>
      </CollapsibleCardHeader>
      <CollapsibleCardBody className="border-border-primary">
        {/*
          QA follow-ups R6/item 11 (PR C, U3): `@clay-run/glaze`'s
          `ReorderableList` (v0.20260821.1, `reorderable-list.tsx:41`)
          unconditionally wraps vertical lists in a hardcoded
          `overflow-y-auto` div — its `className` prop only reaches the
          *inner* div, so there's no prop-based way to opt out, and no
          `scrollable?`-style escape hatch exists upstream (filed as a
          follow-up, see `docs/playbooks-pr-c-qa-followups.md`). That
          internal scroll container fights dnd-kit's own edge-autoscroll
          during a drag (the "phantom scrollbar" QA item 11 describes) —
          the prototype's equivalent `.rungs` container
          (`playbooks-prototype-v6.html:2084-2085`) is a plain flex
          column with no overflow, which is the target behavior here.
          Verified via devtools against the rendered DOM: this wrapper div
          is the immediate parent of Glaze's `overflow-y-auto` div, so the
          arbitrary-child selector below lands on exactly that element
          without touching anything else. Scoped to this one call site —
          do NOT patch `node_modules` or fork the component.
        */}
        <div className="[&>div]:overflow-visible">
          <ReorderableList
            items={order}
            onReorder={(event) => void handleReorder(event)}
            direction="vertical"
            // A second drag while the first write is in flight would compose
            // against ids the server has already renumbered — see the
            // `handleReorder` comment above (Codex adversarial-review fix, PR B
            // follow-up). The old second gate (a staged addition/removal
            // making any reorder a guaranteed 400) is gone as of the
            // 2026-09-02 write-through rip-out: add/remove now persist
            // immediately, so the drag surface can never compose against a
            // ladder the server hasn't already confirmed.
            disabled={saving}
          >
            {orderedRungs.map((rung, i) => (
              <ReorderableItemWrapper key={rung.id} id={rung.id}>
                <RungRow
                  rung={rung}
                  index={i}
                  library={library}
                  // Codex hardening fix: `saving` gates this even though
                  // `rungInlineCounts`'s own version guard (comparing the
                  // ladder response's `playbookVersionId` against
                  // `headVersionId`) does NOT catch the optimistic-reorder
                  // window. `handleReorder` calls `setOrder(next)` to render
                  // the new drag order immediately, but `ladder`/`headVersionId`
                  // still describe the OLD order until the reorder POST
                  // resolves and the ladder invalidation lands — during that
                  // window both values are still mutually consistent (just
                  // stale), so the guard passes even though the counts were
                  // computed under an order that no longer matches what's on
                  // screen. `saving` is true for exactly that window, so
                  // forcing `null` here falls through to the existing
                  // null-renders-as-em-dash convention instead of showing a
                  // number that's briefly wrong with no visual indication.
                  counts={saving ? null : rungInlineCounts(ladder, rung, headVersionId)}
                  ladder={saving ? undefined : ladder}
                  headVersionId={headVersionId}
                  onPreview={(name) => {
                    // `playbookId` rides on the scope because `MembersPanel` is a
                    // sibling of this card, not a descendant — it has no other
                    // route to the id the ladder query needs (U3). Scoped by the
                    // rung's stable `id`: it addresses exactly one rung and never
                    // moves, so neither a reorder (which rewrites every position)
                    // nor a shared `segmentationId` (the catch-all can borrow an
                    // earlier rung's) can make the scope name one rung and preview
                    // another's members. See `RungScope`'s doc comment.
                    setRungScope({
                      kind: 'rung',
                      rungId: rung.id,
                      rungName: name,
                      playbookId,
                    });
                    // B2: the members panel is collapsed by default — without
                    // this, Preview scopes a table the rep can't see.
                    setQualifyMembersOpen(true);
                  }}
                  onRemove={() => removeAndPersist(rungDraftKey(rung.id))}
                  onDuplicate={() => void duplicateAndPersist(rung)}
                  autoExpand={!!preselectRungId && rung.id === preselectRungId}
                  campaignSfdcId={campaignSfdcId}
                  playbookId={playbookId}
                  playId={playId}
                />
              </ReorderableItemWrapper>
            ))}
          </ReorderableList>
        </div>
        {pendingRungs && pendingRungs.length > 0 && (
          <div>
            {pendingRungs.map((rung, i) => (
              <PendingRungRow
                key={rung.draftKey}
                rung={rung}
                index={orderedRungs.length + i}
                library={library}
                onRemove={() => removeAndPersist(rung.draftKey)}
                onDuplicate={() => void duplicateAndPersist(rung)}
                playId={playId}
                playbookId={playbookId}
                usageOrigin={rungUsageOrigin({
                  playId,
                  campaignSfdcId,
                  playbookId,
                  // No server id yet — `rungDraftKey` (`new:<n>`) is the
                  // stable identity across the save that mints one.
                  rungId: null,
                  rungDraftKey: rung.draftKey,
                })}
              />
            ))}
          </div>
        )}
        {catchAll && (
          <RungRow
            rung={catchAll}
            index={orderedRungs.length + (pendingRungs?.length ?? 0)}
            library={library}
            // Codex hardening fix — same optimistic-reorder stale-order
            // window as the ranked rungs above; see that call site's comment.
            counts={saving ? null : rungInlineCounts(ladder, catchAll, headVersionId)}
            ladder={saving ? undefined : ladder}
            headVersionId={headVersionId}
            onPreview={(name) => {
              setRungScope({
                kind: 'rung',
                rungId: catchAll.id,
                rungName: name,
                playbookId,
              });
              // B2: same as the ranked rungs' `onPreview` above.
              setQualifyMembersOpen(true);
            }}
            onRemove={null}
            onDuplicate={null}
            autoExpand={!!preselectRungId && catchAll.id === preselectRungId}
            campaignSfdcId={campaignSfdcId}
            playbookId={playbookId}
            playId={playId}
          />
        )}
        <div className="flex justify-end border-t border-border-secondary px-4 py-3">
          <Button variant="primary" size="sm" onClick={onContinue}>
            Continue to Sequences
            <Icon type={ArrowRight} />
          </Button>
        </div>
      </CollapsibleCardBody>
      {addOpen && (
        <PushDrawer title="Add segment" onClose={() => setAddOpen(false)}>
          <AddSegmentationDrawerBody
            playbookId={playbookId}
            playId={playId}
            library={library}
            rungs={rungs}
            pendingRungs={pendingRungs}
            onClose={() => setAddOpen(false)}
          />
        </PushDrawer>
      )}
    </CollapsibleCard>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step
// ─────────────────────────────────────────────────────────────────────────────

export interface QualifyStepProps {
  campaignSfdcId: string;
  /**
   * Optional as of terra-qyt.14 (U9) — `undefined` in playbook mode, where
   * `playbookId` below is the real identity and no `getPlays` lookup runs at
   * all (see `playsQuery`'s `enabled`).
   */
  playId?: string;
  /**
   * terra-qyt.14 (U9) — playbook mode's identity, supplied DIRECTLY by
   * `PlayFlowShell` rather than re-derived by fetching this campaign's plays
   * and finding one whose `playbookId` matches (`play` below) — that lookup
   * is SKIPPED in this mode, not replaced. `undefined` in play mode, where
   * `resolvedPlaybookId` falls back to `play?.playbookId` instead.
   */
  playbookId?: string;
  onGoToSequences: () => void;
  /**
   * `MembersPanel`, built once by `PlayFlowShell` and handed down as a slot
   * (PR B follow-up U5/R5) — `null` when there's no campaignSfdcId (shell
   * renders its own error band in that case instead).
   */
  membersPanel: ReactNode;
  /**
   * Round-3c QA fixes, H2 — the `?rung=` param off a "Where is this used"
   * deep link (`UsagePlacesList`, H1): a rung id to auto-expand on the
   * waterfall below. `null`/unknown/omitted → nothing auto-expands, matching
   * `WaterfallCard`'s `preselectRungId` doc comment. Optional (rather than
   * required) so the many pre-H2 test call sites that render `QualifyStep`
   * directly keep compiling unmodified.
   */
  preselectRungId?: string | null;
  /**
   * terra-qyt.15 (U10) — playbook mode's seed-campaign write, wired by
   * `PlayFlowShell` to `?campaignSfdcId=`. `undefined` in play mode, where
   * `ContextCard` renders `mode: 'locked'` and never calls it.
   */
  onSeedChange?: (campaignSfdcId: string | null) => void;
}

export function QualifyStep({
  campaignSfdcId,
  playId,
  playbookId,
  onGoToSequences,
  membersPanel,
  preselectRungId = null,
  onSeedChange,
}: QualifyStepProps) {
  const { apiFetch } = useApi();
  // U6 (S2) — the segmentation usage gate's decision handlers below write
  // through the same caches the in-place save path invalidates.
  const queryClient = useQueryClient();
  // B2: collapsed by default; a rung's "Preview" (below) opens it. Module-scope
  // rather than local `useState` because that click originates in
  // `WaterfallCard`, a sibling of the `CollapsibleCard` below it — see the
  // store's own doc comment.
  const qualifyMembersOpen = useMembersPanelCollapseStore(selectQualifyMembersOpen);
  // Batch 7 / Unit E1 — the scope tint the host `CollapsibleCard` now carries
  // for the embedded `MembersPanel` (same `rung-scope-store` `RungRow` above
  // reads for its own row tint, and `MembersPanel` itself reads for its
  // non-embedded card tint — one store, three consumers, never out of sync).
  const scope = useRungScopeStore(selectRungScope);

  const libraryQuery = useQuery({
    queryKey: libraryQueryKey(playId),
    queryFn: () => getLibrary(apiFetch, { playId }),
  });

  // terra-qyt.14 (U9) — SKIPPED (not merely disabled-and-ignored) in
  // playbook mode: `playbookId` is already the real identity, so there is no
  // Play to resolve one from. Do-Not-Create: no `getPlays` call in playbook
  // mode.
  const playsQuery = useQuery({
    queryKey: playsQueryKey(campaignSfdcId),
    queryFn: () => getPlays(apiFetch, { campaignSfdcId }),
    enabled: !!campaignSfdcId && playbookId === undefined,
  });

  const play = playsQuery.data?.plays.find((p) => p.id === playId) ?? null;
  // terra-qyt.14 (U9) — one expression, not a new hook: playbook mode's
  // caller-supplied id wins outright; play mode falls back to the Play this
  // campaign's `getPlays` list resolved.
  const resolvedPlaybookId = playbookId ?? play?.playbookId;

  const playbookQuery = useQuery({
    queryKey: playbookDetailQueryKey(resolvedPlaybookId ?? '', playId),
    queryFn: () => getPlaybook(apiFetch, resolvedPlaybookId!, { playId }),
    enabled: !!resolvedPlaybookId,
  });

  // R7: same cache key `MembersPanel`/`ReviewStep` use for this Playbook's
  // ladder — fetches once and is shared, not a duplicate. Deliberate side
  // effect (Decision 6): the ladder now fetches as soon as Qualify renders a
  // waterfall, not only after a Preview click — that's the feature.
  const ladderQuery = useMatchedMembersLadder(campaignSfdcId, resolvedPlaybookId ?? null, playId);

  /**
   * The staged playbook draft (PR C, U3C — see `playbook-draft-store.ts`).
   * Seeded here AND in `ReviewStep` (both idempotent, per-playbook-id) so
   * whichever step the rep opens first has one to edit/review.
   */
  const draft = usePlaybookDraftStore(selectPlaybookDraft);
  useEffect(() => {
    // `playbookQuery.data !== undefined` (not just `resolvedPlaybookId`) is
    // the real gate — see `SequencesStep`'s identical effect for the full
    // race-condition writeup (Codex-hardening fix found while building U5):
    // seeding with a premature `?? null` head permanently strands this
    // playbookId on an empty draft once the real head arrives a tick later.
    if (resolvedPlaybookId && playbookQuery.data !== undefined) {
      // Plan 002, U6 (Decision 16) — in a play the draft writes the play's ladder.
      ensurePlaybookDraft(resolvedPlaybookId, playbookQuery.data.head, playId ?? null);
    }
  }, [resolvedPlaybookId, playbookQuery.data, playId]);

  const headRungs = playbookQuery.data?.head?.rungs ?? [];
  /**
   * `WaterfallCard`'s `rungs` prop is the draft's PERSISTED view: the head's
   * own rungs, minus any the rep just removed (write-through, 2026-09-02
   * rip-out — the removal has already round-tripped through
   * `removePlaybookDraftRung`'s `persistLadder` call by the time this reads
   * it, or is optimistically applied for the brief in-flight window before
   * that POST resolves). `null` (draft not seeded yet — the effect above
   * hasn't run its first time) falls back to the head verbatim rather than a
   * one-frame empty flash. U3C never stages an EDIT to an already-persisted
   * rung's fields (that's the
   * Sequences step's inline sender control — U5's `SenderModal` originally,
   * replaced PR C / U8 by an inline popover in `SequencesStep.tsx`), so the
   * head's own rung objects are reused as-is — only which ones are present
   * can differ from `headRungs`.
   */
  const draftRungIds =
    draft === null ? null : new Set(draft.rungs.map((r) => r.id).filter((id): id is string => id !== undefined));
  const visibleRungs = draftRungIds === null ? headRungs : headRungs.filter((r) => draftRungIds.has(r.id));
  /**
   * Same persisted rungs, with any staged sender edit (written via
   * `setPlaybookDraftRungSender` from the Sequences step's inline sender
   * popover, PR C / U8 — U5's `SenderModal` originally) overlaid onto
   * `mapping` — the one field U3C's own comment above flagged as staged
   * elsewhere. Only `mapping` is replaced; every structural field (`id`,
   * `position`, `isCatchAll`, `segmentationId`) still comes from the head
   * rung, so this cannot desync from `WaterfallCard`'s reorder/signature
   * logic, which never reads `mapping`.
   */
  const draftRungById = new Map((draft?.rungs ?? []).map((r) => [r.id, r]));
  const displayRungs = visibleRungs.map((rung) => {
    const staged = draftRungById.get(rung.id);
    return staged ? { ...rung, mapping: staged.mapping } : rung;
  });
  const pendingRungs = draft?.rungs.filter((r) => r.id === undefined) ?? [];
  // terra-qyt.15 (U10) — playbook mode always shows the seed-campaign card,
  // even with no seed picked yet (its own empty-seed copy covers that state);
  // play mode's card is always the locked, non-editable one. `CoverageCard`
  // needs a real campaign to count against, so it only renders once a seed
  // (or a play's fixed campaign) is actually set.
  const contextCardMode: 'locked' | 'seed' = playbookId !== undefined ? 'seed' : 'locked';
  const hasCampaign = campaignSfdcId !== '';

  // ───────────────────────────────────────────────────────────────────────────
  // U6 (S2) / Decision 13 — the segmentation usage gate's decision handlers.
  //
  // Hoisted to STEP level (not per row) for the same reason
  // `SequencesStep.tsx:2469-2473` hoists its own: a row collapsing, or the
  // whole waterfall re-rendering, must not take the pending decision's
  // executor down with it. Both handlers read everything they need off
  // `pending` — never a closure over "the" expanded row.
  //
  // The origin key is derived FROM the pending request rather than from a
  // notion of "the currently expanded rung" (which this step, unlike
  // `SequencesStep`, does not have — each `RungRow` owns its own `expanded`
  // state). The guards below are exactly the ownership test: a SEGMENTATION
  // save parked under a rung origin on THIS playbook is this step's to
  // execute, and nothing else is. `''` never matches a real origin key
  // (which always starts `play:`/`library:`).
  // ───────────────────────────────────────────────────────────────────────────
  const pendingUsageSidebar = useUsageSidebarStore(selectUsageSidebar);
  const segmentationDecisionOriginKey =
    pendingUsageSidebar !== null &&
    pendingUsageSidebar.mode === 'save' &&
    pendingUsageSidebar.target.kind === 'segmentation' &&
    pendingUsageSidebar.origin.kind !== 'library' &&
    pendingUsageSidebar.origin.playbookId === resolvedPlaybookId
      ? originKeyOf(pendingUsageSidebar.origin)
      : '';

  /**
   * Plan 2026-09-28-006 U5 (rulebook R5, R6) — true when the pending's target
   * is THIS play's own segmentation (a copy or a play-created original).
   * `saveMenuOptions` reads the same `target.owningPlayId`, so `saveMenuOptions`
   * never offers ③ (`'update-all'`) for it in practice — this guard is
   * defense in depth, mirroring `SequencesStep.tsx#isOwnedTarget`.
   */
  function isSegmentationOwnedTarget(pending: PendingUsageSidebar): boolean {
    return pending.origin.kind === 'play' && pending.target.owningPlayId === pending.origin.playId;
  }

  /**
   * "Update all N" — writes the rep's draft conditions onto the SHARED head,
   * acknowledging the FRESH `pending.places.length` (not the frozen
   * `acknowledgedCount`, which a `shared_mapping_conflict` retry deliberately
   * leaves alone while `refreshUsageSidebarUsage` updates `places`). Same
   * invalidation set the in-place save path runs.
   */
  async function handleSegmentationUpdateAll(
    pending: PendingUsageSidebar,
    isStillCurrent: () => boolean,
  ): Promise<void> {
    // Plan 2026-09-27-006 U7b — reachable in a play now: `SegmentationConditionsEditor#saveWithGate`
    // opens this same sidebar for a SHARED Library segmentation. "Update all"
    // is still a plain template write, unconditionally without `{ playId }`
    // below — it writes the Library row every other place sees, exactly like
    // the playbook doorway's own "Update all".
    if (pending.target.kind !== 'segmentation') return;
    const conditions = pending.draftConditions;
    if (conditions == null || pending.headVersionId === null) return;
    // [R5][R6] `saveMenuOptions` never shows ③ for an owned target — there is
    // no playbook to update.
    if (isSegmentationOwnedTarget(pending)) {
      throw new Error('unreachable: an owned segment is used by one group and is never gated (R11)');
    }
    await saveSegmentationConditions(
      apiFetch,
      pending.target.id,
      conditions,
      pending.headVersionId,
      pending.places.length,
    );
    // The write above committed regardless; only the client-side continuation
    // is guarded against a request a later `openUsageSidebar` replaced.
    if (!isStillCurrent()) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: segmentationDetailQueryKey(pending.target.id) }),
      queryClient.invalidateQueries({ queryKey: libraryQueryKey() }),
      queryClient.invalidateQueries({ queryKey: matchedMembersPrefixKey() }),
    ]);
    addToast({ content: 'Conditions saved.', status: 'success' });
  }

  /**
   * "Save a new version" — forks the segmentation WITH the draft conditions
   * (one server write, so the fork never carries the source's stale
   * conditions) and remaps ONLY the origin rung onto the fork. Every other
   * place keeps the original, which is the whole point of this branch.
   *
   * A `version_conflict` from the fork (the SOURCE head moved after the draft
   * was edited against it) is RETHROWN untouched — `use-usage-sidebar-decision.ts`'s
   * own catch parks the Save panel stale with the server's sentence and the
   * rep's conditions (R14, plan 2026-09-28-008 U2); nothing refetches until
   * the rep presses Reload. Its retry policy covers
   * `shared_mapping_conflict` only, matching the sequence handler.
   */
  async function handleSegmentationSaveNew(
    pending: PendingUsageSidebar,
    isStillCurrent: () => boolean,
  ): Promise<void> {
    if (pending.target.kind !== 'segmentation') return;
    // A library origin belongs to `PlaybooksConfigPage`'s own hook (it has no
    // rung to remap) — the same single exclusion the sequence handlers make.
    if (pending.origin.kind === 'library') return;
    const conditions = pending.draftConditions;
    if (conditions == null) return;
    // Plan 2026-09-27-006 U7b — a play's "Save as new segment" is
    // `ensurePlayOwned` + a direct write on the owned copy: the fork response
    // already rewrites the origin rung's ladder onto the copy, so there is no
    // separate duplicate + remap the way the playbook doorway needs below.
    if (pending.origin.kind === 'play') {
      return handlePlaySegmentationSaveNew(pending, conditions, isStillCurrent);
    }

    const created = await duplicateSegmentation(apiFetch, pending.target.id, {
      conditions,
      expectedSourceVersionId: pending.headVersionId ?? undefined,
    });

    const { playbookId: originPlaybookId, rungDraftKey: originRungDraftKey } = pending.origin;
    try {
      const response = await remapPlaybookDraftRungSegmentation(
        apiFetch,
        originPlaybookId,
        originRungDraftKey,
        created.id,
      );
      if (!isStillCurrent()) return;
      if (response) applyLadderSaveToCache(queryClient, originPlaybookId, response);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: libraryQueryKey() }),
        queryClient.invalidateQueries({ queryKey: matchedMembersPrefixKey() }),
      ]);
      // Codex round-2 review (wave 2-3, finding 2) — `response` can be `null`
      // while the fork above still committed (the rung/draft/playbook moved
      // out from under this request), so a bare `response` truthiness check
      // reported false successes. Mirrors the sequence handler's own
      // `remapped` disambiguation immediately below.
      const remapped = response
        ? response.rungs.some((r) => r.segmentationId === created.id)
        : draftRungMapsToSegmentation(originPlaybookId, originRungDraftKey, created.id);
      if (remapped) {
        addToast({ content: 'Saved a new version. This group now uses it.', status: 'success' });
      } else {
        // Mirrors the sequence handler's own split failure message: the fork IS
        // saved; only the rung switch failed, and that is separately recoverable.
        addErrorToast({
          content:
            "The new version was saved, but this group couldn't be switched to it. Try editing its conditions again.",
        });
      }
    } catch {
      if (!isStillCurrent()) return;
      // Mirrors the sequence handler's own split failure message: the fork IS
      // saved; only the rung switch failed, and that is separately recoverable.
      addErrorToast({
        content:
          "The new version was saved, but this group couldn't be switched to it. Try editing its conditions again.",
      });
    }
  }

  /**
   * Plan 2026-09-27-006 U7b — the play-mode half of "Save as new segment"
   * (dispatched from {@link handleSegmentationSaveNew} above). No separate
   * remap: unlike the playbook doorway, the fork already rewrites the origin
   * rung's ladder onto the copy.
   *
   * Plan 2026-09-28-003 — the fork is adopted (the rung repointed onto the
   * copy) only AFTER the copy carries the rep's conditions and the client
   * already holds that written head in the segmentation-detail cache
   * (`savePlaySegmentationConditions`'s whole job). Before this fix, the fork
   * was adopted first — the still-mounted editor retargeted onto the copy and
   * reseeded its draft from the copy's stale PRE-EDIT (Library) head before
   * the conditions write had even been issued, so the group appeared to show
   * the old filters with Save lit up as unsaved even though the server had
   * the rep's edit the whole time. See
   * plans/2026-09-28-003-pr1694-segment-save-as-new-stale-ui-plan.md.
   *
   * CAS token: `pending.headVersionId` — the Library head the sidebar's
   * pending request was recorded against, matching `expectedSourceVersionId`
   * on the fork itself (Key Technical Decision 11) — a moved Library head
   * surfaces `version_conflict` and leaves the rung unchanged.
   */
  async function handlePlaySegmentationSaveNew(
    pending: PendingUsageSidebar,
    conditions: SegmentationConditions,
    isStillCurrent: () => boolean,
  ): Promise<void> {
    if (pending.origin.kind !== 'play' || pending.target.kind !== 'segmentation') return;
    const { playId: originPlayId, playbookId: originPlaybookId, rungId: originRungId } = pending.origin;
    if (pending.headVersionId === null) return;
    // [R20] A rung added this session has no server id yet — a per-group
    // copy needs the group to exist first.
    if (originRungId === null) {
      addErrorToast({ content: SAVE_GROUP_FIRST });
      return;
    }
    // Plan 2026-09-28-008 U2 (R7, R14; Codex round-3 F7) — a failure is
    // rethrown, never swallowed: the decision hook settles it in one place
    // (a 409 parks the pending stale holding the rep's conditions; anything
    // else resets it to idle and toasts once). Swallowing it here reported
    // success to the dispatcher, which closed the pending.
    // Plan 2026-09-27-007, U5 (A4 P2) — this write is unconditional
    // regardless of whether a later `openUsageSidebar` call has since
    // replaced this request (it runs inside the helper, ahead of any
    // `isStillCurrent()` check) — real work that must land or the copy is
    // left with no conditions ever saved onto it (a half-applied "Save as
    // new"). Only the client-side continuation below (invalidations,
    // toast) is guarded — same shape as `handleSegmentationUpdateAll`
    // above (D15).
    const saved = await savePlaySegmentationConditions(apiFetch, queryClient, {
      playId: originPlayId,
      playbookId: originPlaybookId,
      segmentationId: pending.target.id,
      // [R3] Forks for THIS group only — a copy-of-copy when the target is
      // already this play's own (a copy two groups shared, "Save as new").
      rungId: originRungId,
      mode: 'fork',
      conditions,
      expectedVersionId: pending.headVersionId,
      confirmedPlaceCount: pending.places.length,
      // Plan 2026-09-29-001 U1 (R3/R5/R14) — a fresh fork is used by exactly
      // one group (this one); the set it was checked against is `[rungId]`.
      confirmedRungIds: [originRungId],
    });
    if (!isStillCurrent()) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: segmentationDetailQueryKey(saved.ownedId) }),
      queryClient.invalidateQueries({ queryKey: libraryQueryKey(originPlayId) }),
      queryClient.invalidateQueries({ queryKey: matchedMembersPrefixKey() }),
    ]);
    addToast({ content: 'Saved a copy for this play. This group now uses it.', status: 'success' });
  }

  /**
   * Plan 2026-09-28-006 U5 (rulebook R8) — option ② of the in-play Save menu
   * for a segmentation: "Update N plays" on a Library target. An OWNED target
   * reaches here only as the one "Save" (`saveMenuOptions`, owned + one group)
   * of a pending the rep's Reload re-armed onto the group's copy (plan
   * 2026-09-28-008 U2): the rep's conditions are written onto that copy in
   * place.
   *
   * A Library target posts `POST /plays/:id/update-plays`: every unsent play
   * still sharing the Library segmentation gets its own copy per group,
   * carrying the change; the Library is never written. The request names the
   * confirmed set — `confirmedFanOutSet(pending.places, origin.rungId)` over
   * the FRESH places, the same set the button label counts. The origin play's
   * rewritten ladder is adopted (`adoptUpdatePlaysResult`, the origin rung
   * last), and `matchedMembersPrefixKey()` is invalidated because a
   * segmentation change can change who each play sends to (edge (7)).
   *
   * Errors: `shared_mapping_conflict` is rethrown untouched — the decision
   * hook refreshes the list and reopens the menu once. `version_conflict`
   * (the Library piece moved) is rethrown untouched too — the hook parks the
   * Save panel stale with the server's sentence (R14, plan 2026-09-28-008
   * U2); only the rep's Reload refetches.
   */
  async function handleSegmentationUpdatePlays(
    pending: PendingUsageSidebar,
    isStillCurrent: () => boolean,
  ): Promise<void> {
    if (pending.origin.kind !== 'play' || pending.target.kind !== 'segmentation') return;
    const origin = pending.origin;
    const conditions = pending.draftConditions;
    if (conditions == null || pending.headVersionId === null) return;
    // Plan 2026-09-29-001 U1 (R3/R5/R14) — hoisted above BOTH branches below
    // (the owned in-place write and the Library fan-out) so a rung staged
    // this session (no server id yet) is refused the same way regardless of
    // which branch would otherwise have run.
    if (origin.rungId === null) {
      addErrorToast({ content: SAVE_GROUP_FIRST });
      return;
    }
    const originRungId = origin.rungId;
    if (isSegmentationOwnedTarget(pending)) {
      // Plan 2026-09-28-008 U2 (R14) — reachable now: the rep's Reload
      // re-arms a stale pending onto the group's CURRENT piece, which can be
      // this play's own copy (another editor copied it for this group). The
      // menu's one "Save" (owned, one group) writes the rep's conditions onto
      // that copy in place, quoting the fresh head Reload fetched.
      await saveSegmentationConditions(
        apiFetch,
        pending.target.id,
        conditions,
        pending.headVersionId,
        pending.places.length,
        {
          playId: origin.playId,
          // Plan 2026-09-29-001 U1 (R3/R5/R14) — the group this save is for,
          // and the fan-out the rep was shown (falling back to just this
          // group when the origin play's own entry is empty/absent).
          group: {
            rungId: originRungId,
            confirmedRungIds: confirmedFanOutSet(pending.places, originRungId).plays.find((p) => p.playId === origin.playId)?.rungIds ?? [originRungId],
          },
        },
      );
      if (!isStillCurrent()) return;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: segmentationDetailQueryKey(pending.target.id) }),
        queryClient.invalidateQueries({ queryKey: libraryQueryKey(origin.playId) }),
        queryClient.invalidateQueries({ queryKey: matchedMembersPrefixKey() }),
      ]);
      addToast({ content: 'Conditions saved.', status: 'success' });
      return;
    }
    const headVersionId = pending.headVersionId;
    const change: PlaybooksUpdatePlaysRequest['change'] = { field: 'conditions', conditions };
    const result = await updatePlays(apiFetch, origin.playId, {
      kind: 'segmentation',
      identityId: pending.target.id,
      expectedSourceVersionId: headVersionId,
      confirmed: confirmedFanOutSet(pending.places, originRungId),
      change,
    });
    // The fan-out is real whether or not this request is still current —
    // the origin play's new ladder is adopted unconditionally.
    await adoptUpdatePlaysResult(queryClient, {
      playbookId: origin.playbookId,
      playId: origin.playId,
      kind: 'segmentation',
      fromId: pending.target.id,
      result,
    });
    await queryClient.invalidateQueries({ queryKey: matchedMembersPrefixKey() });
    if (!isStillCurrent()) return;
    const plays = result.updatedPlays.length;
    const originEntry = result.updatedPlays.find((entry) => entry.playId === origin.playId);
    addToast({
      content:
        plays > 1
          ? `Updated ${pluralize(plays, 'play')}.`
          : `Updated ${pluralize(originEntry?.rungs.length ?? 1, 'group')} in this play.`,
      status: 'success',
    });
  }

  useUsageSidebarDecision(segmentationDecisionOriginKey, {
    updateAll: handleSegmentationUpdateAll,
    updatePlays: handleSegmentationUpdatePlays,
    saveNew: handleSegmentationSaveNew,
  });

  return (
    <div className="qualify-grid flex flex-col gap-4 p-6 lg:grid lg:grid-cols-[20rem_1fr] lg:items-start">
      <div className="flex flex-col gap-3">
        <ContextCard campaignSfdcId={campaignSfdcId} mode={contextCardMode} onSeedChange={onSeedChange} />
        {hasCampaign && <CoverageCard campaignSfdcId={campaignSfdcId} />}
      </div>
      <div className="flex flex-col gap-3">
        {/*
          B2 (plan `2026-09-01-001`, Batch 11): `membersPanel` moved here from
          the left column, into the slot the old `AudienceRuleCard` occupied
          before B1 relocated that card behind the disabled "Audience Rules"
          tab. Collapsed by default (`members-panel-collapse-store.ts`) — a
          rung's "Preview" click above expands it.
        */}
        {membersPanel && (
          <CollapsibleCard
            open={qualifyMembersOpen}
            onOpenChange={setQualifyMembersOpen}
            // Batch 7 / Unit E1 — this card is now the ONLY header/border the
            // members panel shows (it mounts `MembersPanel` `embedded`, see
            // `PlayFlowShell`), so it also carries the scope tint
            // `MembersPanel` itself applies in its non-embedded (Sequences)
            // mount. 2026-09-16 Fix 3 — that tint is now the selected-state
            // pair (see `RungRow` above and `MembersPanel.tsx`'s header).
            className={scope !== null ? '!border-border-selected !bg-bg-selected' : 'border-border-primary'}
          >
            <CollapsibleCardHeader>
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Text size="sm" weight="semibold">
                    Members
                  </Text>
                  {/*
                    Batch 7 / Unit E1 — the matched-count text and "Currently
                    previewing {name}" / "Hide preview" cluster that used to
                    live inside `MembersPanel`'s own (now-dropped) heading.
                    Same derivation, shared cache/store — see
                    `MembersPanel.tsx`'s `useMembersPanelScopeState`.
                  */}
                  <MembersPanelHeaderStatus campaignSfdcId={campaignSfdcId} />
                </div>
                <Text size="xs" variant="tertiary">
                  Click Preview on a group above to see the members it matches.
                </Text>
              </div>
            </CollapsibleCardHeader>
            {/*
              2026-09-16 Fix 2 — `p-4`, matching every other
              `CollapsibleCardBody` in this app (Glaze's own ships with NO
              padding: it is a bare `border-t` panel). Without it the embedded
              panel's scope caption rendered flush against the card's left
              edge, outside the padding this card's own header sits in — its
              own band between the header and the table.
            */}
            <CollapsibleCardBody className="flex flex-col gap-2 p-4 border-border-primary">
              {membersPanel}
            </CollapsibleCardBody>
          </CollapsibleCard>
        )}
        {playbookId === undefined && playsQuery.isLoading && (
          <div className="flex justify-center p-6">
            <LoadingSpinner size="md" />
          </div>
        )}
        {playbookId === undefined && playsQuery.isError && (
          <Alert status="error">Couldn&apos;t load this play. Please try again.</Alert>
        )}
        {playbookId === undefined && !playsQuery.isLoading && !playsQuery.isError && !play && (
          <div className="rounded-lg border border-border-primary bg-bg-primary p-4">
            <Text size="sm" variant="tertiary">
              This Play wasn&apos;t found for this campaign — open it again from the campaign home page.
            </Text>
          </div>
        )}
        {resolvedPlaybookId && playbookQuery.isLoading && (
          <div className="flex justify-center p-6">
            <LoadingSpinner size="md" />
          </div>
        )}
        {resolvedPlaybookId && playbookQuery.isError && (
          <Alert status="error">Couldn&apos;t load this Play&apos;s Playbook. Please try again.</Alert>
        )}
        {resolvedPlaybookId && playbookQuery.data && (
          <WaterfallCard
            campaignSfdcId={campaignSfdcId}
            playbookId={resolvedPlaybookId}
            playId={playId}
            headVersionId={playbookQuery.data.head?.versionId ?? null}
            rungs={displayRungs}
            library={libraryQuery.data}
            ladder={ladderQuery.data}
            onContinue={onGoToSequences}
            pendingRungs={pendingRungs}
            preselectRungId={preselectRungId}
          />
        )}
      </div>
    </div>
  );
}
